import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { localStorageManager, STORAGE_KEYS } from '../../utils/localStorage';
import { openModalWithHash, closeModalWithHash } from '../../utils/hashRouting';
import { TokenCategory } from '../data/types';

export type TimeFilterType = '1h' | '2h' | '12h' | '24h' | '3D' | '7D' | 'ALL';

export interface FilterFormData {
  timeFilter: TimeFilterType;
  customTimeMinutes?: { min?: number; max?: number };
  lastUpdatedTimeFilter: TimeFilterType;
  lastUpdatedCustomTimeMinutes?: { min?: number; max?: number };
  currentAfCnt: { min?: number; max?: number };
  athAfCnt: { min?: number; max?: number };
  atlMc: { min?: number; max?: number };
  athMc: { min?: number; max?: number };
  mc: { min?: number; max?: number };
  currentTokenAmountInDecimals: { min?: number; max?: number };
  athTokenAmountInDecimals: { min?: number; max?: number };
  totalUsedUsd: { min?: number; max?: number };
  devClusterCnt: { min?: number; max?: number };
  cumulativeConvictionAtSameTimeRank: { min?: number; max?: number };
}

interface UIState {
  selectedToken: {
    tokenAddress: string;
    symbol?: string;
  };
  maxCardCount: number;
  timeFilter: TimeFilterType;
  searchQuery: string;
  searchResultTokenAddresses: string[] | null;
  isSearchMode: boolean;
  isTokenDetailModalOpen: boolean;
  isSocialDetailModalOpen: boolean;
  isNoteDetailModalOpen: boolean;
  activeFilters: FilterFormData | null;
  // 덱별 토큰 개수 (deckId -> tokenCount)
  deckTokenCounts: Record<string, number>;
  isTwitterSyncModalOpen: boolean;
  createNoteModal: {
    tokenSymbol: string;
    tokenAddress: string;
    tokenName?: string;
    tokenTwitter?: string;
    tokenWebsite?: string;
    tokenCategory?: TokenCategory;
    syncId?: string;
    existingAssociatedHandles?: string[];
    existingCommunityId?: string;
    onSuccess?: (syncId: string) => void;
  } | null;
  setSelectedToken: (token: {  tokenAddress: string; symbol?: string }) => void;
  setMaxCardCount: (count: number) => void;
  setTimeFilter: (filter: TimeFilterType) => void;
  setSearchQuery: (query: string) => void;
  setSearchResults: (tokenAddresses: string[] | null) => void;
  setIsSearchMode: (isSearchMode: boolean) => void;
  clearSearch: () => void;
  openTokenDetailModal: () => void;
  closeTokenDetailModal: () => void;
  openSocialDetailModal: () => void;
  closeSocialDetailModal: () => void;
  openNoteDetailModal: () => void;
  closeNoteDetailModal: () => void;
  openCreateNoteModal: (data: NonNullable<UIState['createNoteModal']>) => void;
  closeTwitterSyncModal: () => void;
  setActiveFilters: (filters: FilterFormData | null) => void;
  clearFilters: () => void;
  // 덱별 토큰 개수 관리
  setDeckTokenCount: (deckId: string, count: number) => void;
  getTotalTokenCount: () => number;
}

export type UserType = 'J' | 'H' | 'S';

// UI State 인터페이스 (로컬 스토리지에 저장되는 데이터)
interface UIStateData {
  maxCardCount: number;
  user?: UserType;
}

// 로컬 스토리지에서 UI State 로드
const loadUIStateFromStorage = (): UIStateData => {
  const stored = localStorageManager.getItem<UIStateData>(STORAGE_KEYS.UI_STATE);
  return {
    maxCardCount: stored?.maxCardCount || 50, // 기본값 50
    user: stored?.user || 'J', // 기본값 J
  };
};

// 로컬 스토리지에 UI State 저장
const saveUIStateToStorage = (state: UIStateData): void => {
  try {
    // 기존 상태를 로드하여 user 필드 유지
    const currentState = localStorageManager.getItem<UIStateData>(STORAGE_KEYS.UI_STATE) || { maxCardCount: 50, user: 'J' };
    localStorageManager.setItem(STORAGE_KEYS.UI_STATE, {
      ...currentState,
      ...state,
    });
  } catch (error) {
    console.error('Failed to save UI state to storage:', error);
  }
};

const getInitialState = (): Partial<UIState> => {
  const uiState = loadUIStateFromStorage();
  return {
    maxCardCount: uiState.maxCardCount,
  };
};

export const useUIStore = create<UIState>()(devtools(
  subscribeWithSelector((set, get) => ({
    selectedToken: {
      tokenAddress: '',
      symbol: '',
    },
    maxCardCount: getInitialState().maxCardCount ?? 50,
    timeFilter: '24h' as TimeFilterType,
    searchQuery: '',
    searchResultTokenAddresses: null,
    isSearchMode: false,
    isTokenDetailModalOpen: false,
    isSocialDetailModalOpen: false,
    isNoteDetailModalOpen: false,
    activeFilters: null,
    deckTokenCounts: {},
    isTwitterSyncModalOpen: false,
    createNoteModal: null,
    setSelectedToken: (token) => set({ selectedToken: token }),
    setMaxCardCount: (count) => {
      set({ maxCardCount: count });
      // 로컬 스토리지에 저장
      saveUIStateToStorage({ maxCardCount: count });
    },
    setTimeFilter: (filter) => set({ timeFilter: filter }),
    setSearchQuery: (query: string) => set({ searchQuery: query }),
    setSearchResults: (tokenAddresses: string[] | null) => set({ searchResultTokenAddresses: tokenAddresses }),
    setIsSearchMode: (isSearchMode: boolean) => set({ isSearchMode }),
    clearSearch: () => set({ 
      searchQuery: '', 
      searchResultTokenAddresses: null, 
      isSearchMode: false 
    }),
    openTokenDetailModal: () => {
      const state = get();
      // 다른 모달이 열려있으면 닫기
      if (state.isSocialDetailModalOpen) {
        set({ isSocialDetailModalOpen: false });
      }
      if (state.isNoteDetailModalOpen) {
        set({ isNoteDetailModalOpen: false });
      }
      
      // selectedToken이 있으면 해시 설정
      if (state.selectedToken) {
        openModalWithHash(state.selectedToken.tokenAddress, 'token');
      }
      
      set({ isTokenDetailModalOpen: true });
    },
    closeTokenDetailModal: () => {
      closeModalWithHash();
      set({ isTokenDetailModalOpen: false, selectedToken: { tokenAddress: '', symbol: '' } });
    },
    openSocialDetailModal: () => {
      const state = get();
      // 다른 모달이 열려있으면 닫기
      if (state.isTokenDetailModalOpen) {
        set({ isTokenDetailModalOpen: false });
      }
      if (state.isNoteDetailModalOpen) {
        set({ isNoteDetailModalOpen: false });
      }
      
      // tokenData에서 tokenAddress 추출하여 해시 설정
      if (state.selectedToken) {
        openModalWithHash(state.selectedToken.tokenAddress, 'social');
      }
      
      set({ 
        isSocialDetailModalOpen: true
      });
    },
    closeSocialDetailModal: () => {
      closeModalWithHash();
      set({ 
        isSocialDetailModalOpen: false, selectedToken: { tokenAddress: '', symbol: '' }
      });
    },
    openNoteDetailModal: () => {
      const state = get();
      // 다른 모달이 열려있으면 닫기
      if (state.isTokenDetailModalOpen) {
        set({ isTokenDetailModalOpen: false });
      }
      if (state.isSocialDetailModalOpen) {
        set({ isSocialDetailModalOpen: false });
      }
      
      // tokenAddress로 해시 설정
      if (state.selectedToken) {
        openModalWithHash(state.selectedToken.tokenAddress, 'note');
      }
      
      set({ 
        isNoteDetailModalOpen: true
      });
    },
    closeNoteDetailModal: () => {
      closeModalWithHash();
      set({ 
        isNoteDetailModalOpen: false, 
        selectedToken: { tokenAddress: '', symbol: '' }
      });
    },
    openCreateNoteModal: (payload: NonNullable<UIState['createNoteModal']>) => {
      set({
        isTwitterSyncModalOpen: true,
        createNoteModal: payload,
      });
    },
    closeTwitterSyncModal: () => {
      set({
        isTwitterSyncModalOpen: false,
        createNoteModal: null,
      });
    },
    setActiveFilters: (filters) => {
      set({ activeFilters: filters });
    },
    clearFilters: () => {
      set({ activeFilters: null });
    },
    // 덱별 토큰 개수 설정
    setDeckTokenCount: (deckId: string, count: number) => {
      set((state) => ({
        deckTokenCounts: {
          ...state.deckTokenCounts,
          [deckId]: count,
        },
      }));
    },
    // 모든 덱의 토큰 개수 합계 계산
    getTotalTokenCount: () => {
      const state = get();
      return Object.values(state.deckTokenCounts).reduce((sum, count) => sum + count, 0);
    },
  })),
  { 
    name: 'UIStore',
    enabled: process.env.NODE_ENV === 'development'
  }
));
